import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Calendar, MapPin, Pencil, Trash2, CreditCard, ShieldCheck, Star } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LangContext";
import { CATEGORY_META, CURRENCY_SYMBOL } from "@/data/categories";
import { priceLabel, fmt } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { PayoutPanel } from "@/components/PayoutPanel";
import { FoundingBadge } from "@/components/FoundingBadge";
import { RatingStars } from "@/components/RatingStars";
import { AvatarUpload } from "@/components/AvatarUpload";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

const STATUS_STYLES = {
  pending: "st-pending",
  accepted: "st-accepted",
  declined: "st-declined",
  paid: "st-paid",
  completed: "st-completed",
  cancelled: "st-cancelled",
};

export default function Dashboard() {
  const { user } = useAuth();
  const { t, tc } = useLang();
  const navigate = useNavigate();
  const [myListings, setMyListings] = useState([]);
  const [customerBookings, setCustomerBookings] = useState([]);
  const [providerBookings, setProviderBookings] = useState([]);
  const [myReviews, setMyReviews] = useState([]);
  const [reviewFor, setReviewFor] = useState(null);
  const [reviewForm, setReviewForm] = useState({ rating: 5, comment: "" });

  const submitReview = async () => {
    try {
      await api.post("/reviews", {
        booking_id: reviewFor.id,
        rating: reviewForm.rating,
        comment: reviewForm.comment,
      });
      toast.success(t("rev_thanks"));
      setReviewFor(null);
      setReviewForm({ rating: 5, comment: "" });
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const load = useCallback(async () => {
    const [l, c, p, r] = await Promise.all([
      api.get("/listings/mine").then((r) => r.data).catch(() => []),
      api.get("/bookings", { params: { role: "customer" } }).then((r) => r.data).catch(() => []),
      api.get("/bookings", { params: { role: "provider" } }).then((r) => r.data).catch(() => []),
      api.get("/reviews/mine").then((r) => r.data).catch(() => []),
    ]);
    setMyListings(l);
    setCustomerBookings(c);
    setProviderBookings(p);
    setMyReviews(r);
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const setStatus = async (id, status) => {
    try {
      await api.patch(`/bookings/${id}/status`, { status });
      toast.success(t("toast_saved"));
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const deleteListing = async (id) => {
    try {
      await api.delete(`/listings/${id}`);
      toast.success(t("toast_saved"));
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const payBooking = async (booking) => {
    try {
      const { data } = await api.post("/payments/checkout", {
        booking_id: booking.id,
        origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const StatusBadge = ({ s }) => (
    <Badge className={`rounded-xl border-0 ${STATUS_STYLES[s] || ""}`}>
      {t(`st_${s}`)}
    </Badge>
  );

  const BookingRow = ({ b, mode }) => {
    const sym = CURRENCY_SYMBOL[b.currency] || b.currency + " ";
    return (
      <div
        data-testid={`booking-row-${b.id}`}
        className="flex flex-col gap-3 rounded-2xl border border-ppc bg-ppc-surface p-5 sm:flex-row sm:items-center sm:justify-between"
      >
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <h4 className="font-display font-medium text-ppc-text">{b.listing_title}</h4>
            <StatusBadge s={b.status} />
            {b.payment_status === "paid" && (
              <Badge className="rounded-xl border-0 st-paid">
                {t("paid_badge")}
              </Badge>
            )}
          </div>
          <p className="mt-1 flex items-center gap-1.5 text-sm text-ppc-muted">
            <Calendar className="h-3.5 w-3.5" /> {b.date} · {b.start_time}–{b.end_time}
          </p>
          <p className="mt-0.5 text-sm text-ppc-muted">
            {mode === "provider" ? b.customer_name : b.provider_name} · {fmt(sym, b.amount)}
          </p>
          {b.message && <p className="mt-1 text-sm italic text-ppc-muted">"{b.message}"</p>}
        </div>
        <div className="flex flex-wrap gap-2">
          {mode === "provider" && b.status === "pending" && (
            <>
              <Button
                size="sm"
                data-testid={`accept-${b.id}`}
                onClick={() => setStatus(b.id, "accepted")}
                className="rounded-xl btn-primary"
              >
                {t("act_accept")}
              </Button>
              <Button
                size="sm"
                variant="outline"
                data-testid={`decline-${b.id}`}
                onClick={() => setStatus(b.id, "declined")}
                className="rounded-xl border-[rgba(240,160,168,0.4)] text-[#f0a0a8]"
              >
                {t("act_decline")}
              </Button>
            </>
          )}
          {mode === "provider" && b.status === "paid" && (
            <Button
              size="sm"
              data-testid={`complete-${b.id}`}
              onClick={() => setStatus(b.id, "completed")}
              className="rounded-xl btn-primary"
            >
              {t("act_complete")}
            </Button>
          )}
          {mode === "customer" && b.status === "accepted" && b.payment_status !== "paid" && (
            <Button
              size="sm"
              data-testid={`pay-${b.id}`}
              onClick={() => payBooking(b)}
              className="gap-1.5 rounded-xl btn-primary"
            >
              <CreditCard className="h-4 w-4" /> {t("act_pay")}
            </Button>
          )}
          {mode === "customer" && b.status === "completed" && !myReviews.some((r) => r.booking_id === b.id) && (
            <Button
              size="sm"
              data-testid={`review-${b.id}`}
              onClick={() => setReviewFor(b)}
              className="gap-1.5 rounded-xl btn-primary"
            >
              <Star className="h-4 w-4" /> {t("rev_rate")}
            </Button>
          )}
          {mode === "customer" && ["pending", "accepted"].includes(b.status) && (
            <Button
              size="sm"
              variant="ghost"
              data-testid={`cancel-${b.id}`}
              onClick={() => setStatus(b.id, "cancelled")}
              className="rounded-xl text-ppc-muted"
            >
              {t("act_cancel")}
            </Button>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="mx-auto max-w-5xl px-5 pb-28 pt-10 md:pb-10 lg:px-8">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <p className="text-sm uppercase tracking-[0.2em] text-ppc-muted">{t("dash_welcome")}</p>
          <h1 className="font-display text-4xl font-medium tracking-tighter text-ppc-text">
            {user?.name || user?.email}
          </h1>
        </div>
        <Button
          onClick={() => navigate("/create-listing")}
          data-testid="dash-new-listing-btn"
          className="gap-1.5 rounded-xl btn-primary"
        >
          <Plus className="h-4 w-4" /> {t("dash_new_listing")}
        </Button>
      </div>

      <div className="mt-6 grid grid-cols-3 gap-2.5" data-testid="dash-stat-row">
        {[
          [customerBookings.length, t("dash_customer_bookings")],
          [providerBookings.length, t("dash_provider_bookings")],
          [myListings.length, t("dash_my_listings")],
        ].map(([n, l]) => (
          <div key={l} className="card-list px-3.5 py-4">
            <p className="text-2xl text-ppc-text">{n}</p>
            <p className="mt-0.5 text-[11.5px] leading-snug text-ppc-muted">{l}</p>
          </div>
        ))}
      </div>

      <Tabs defaultValue="customer" className="mt-6">
        <TabsList className="h-11 rounded-xl border border-ppc bg-ppc-bg p-1">
          <TabsTrigger value="customer" data-testid="tab-customer" className="rounded-xl">
            {t("tab_customer")}
          </TabsTrigger>
          <TabsTrigger value="provider" data-testid="tab-provider" className="rounded-xl">
            {t("tab_provider")}
          </TabsTrigger>
          <TabsTrigger value="payouts" data-testid="tab-payouts" className="rounded-xl">
            {t("tab_payouts")}
          </TabsTrigger>
        </TabsList>

        {/* Customer */}
        <TabsContent value="customer" className="mt-6 space-y-4">
          <h3 className="font-display text-xl font-medium text-ppc-text">
            {t("dash_customer_bookings")}
          </h3>
          {customerBookings.length === 0 ? (
            <p className="rounded-2xl border border-dashed border-ppc bg-ppc-surface p-8 text-center text-ppc-muted">
              {t("dash_no_bookings")}
            </p>
          ) : (
            customerBookings.map((b) => <BookingRow key={b.id} b={b} mode="customer" />)
          )}
        </TabsContent>

        {/* Provider */}
        <TabsContent value="provider" className="mt-6 space-y-8">
          {!user?.verified && (
            <div className="card-focal flex flex-col gap-4 p-5 sm:flex-row sm:items-center sm:justify-between" data-testid="verify-cta-card">
              <div className="min-w-0">
                <p className="flex items-center gap-2 text-base text-ppc-text md:text-lg">
                  <ShieldCheck className="h-5 w-5" style={{ color: "#9184d9" }} /> {t("ver_cta")}
                </p>
                <p className="mt-1.5 max-w-xl text-[13px] leading-relaxed text-ppc-muted">{t("ver_cta_d")}</p>
              </div>
              <Button onClick={() => navigate("/verification")} data-testid="verify-cta-btn" className="btn-primary shrink-0 rounded-xl">
                {t("ver_cta")}
              </Button>
            </div>
          )}
          {user?.verified && (
            <div className="card-list flex items-center gap-2.5 p-5" data-testid="verified-status-card">
              <ShieldCheck className="h-5 w-5" style={{ color: "#9184d9" }} />
              <p className="text-sm text-ppc-text">{t("ver_done")}</p>
              <span className="text-[12px] text-ppc-muted">· {t("ver_done_d")}</span>
            </div>
          )}
          <AvatarUpload />

          {user?.founding_member && (
            <div className="card-focal flex flex-col gap-3 p-5 sm:flex-row sm:items-center sm:justify-between" data-testid="founding-card">
              <div>
                <FoundingBadge fm={user.founding_member} size="lg" />
                <p className="mt-2 text-sm text-ppc-muted">
                  {t("founding_yours").replace("{n}", user.founding_member.number).replace("{city}", user.founding_member.city)}
                </p>
              </div>
              <p className="max-w-sm text-xs text-ppc-muted">{t("founding_d")}</p>
            </div>
          )}
          <div className="space-y-4">
            <h3 className="font-display text-xl font-medium text-ppc-text">
              {t("dash_provider_bookings")}
            </h3>
            {providerBookings.length === 0 ? (
              <p className="rounded-2xl border border-dashed border-ppc bg-ppc-surface p-8 text-center text-ppc-muted">
                {t("dash_no_bookings")}
              </p>
            ) : (
              providerBookings.map((b) => <BookingRow key={b.id} b={b} mode="provider" />)
            )}
          </div>

          <div className="space-y-4">
            <h3 className="font-display text-xl font-medium text-ppc-text">{t("dash_my_listings")}</h3>
            {myListings.length === 0 ? (
              <p className="rounded-2xl border border-dashed border-ppc bg-ppc-surface p-8 text-center text-ppc-muted">
                {t("dash_no_listings")}
              </p>
            ) : (
              <div className="grid gap-4 sm:grid-cols-2">
                {myListings.map((l) => {
                  const price = priceLabel(l, t);
                  return (
                    <div
                      key={l.id}
                      data-testid={`my-listing-${l.id}`}
                      className="overflow-hidden rounded-2xl border border-ppc bg-ppc-surface"
                    >
                      <img
                        src={l.image || CATEGORY_META[l.category]?.image}
                        alt={l.title}
                        className="h-32 w-full object-cover"
                      />
                      <div className="p-4">
                        <p className="text-xs uppercase tracking-wider text-ppc-muted">
                          {tc(l.category)}
                        </p>
                        <h4 className="font-display mt-1 font-medium text-ppc-text line-clamp-1">
                          {l.title}
                        </h4>
                        <p className="mt-1 flex items-center gap-1 text-sm text-ppc-muted">
                          <MapPin className="h-3.5 w-3.5" /> {l.city}
                        </p>
                        <p className="mt-2 font-display font-medium text-primary-green">
                          {price.main}
                          <span className="ml-1 text-xs font-normal text-ppc-muted">{price.unit}</span>
                        </p>
                        <div className="mt-3 flex gap-2">
                          <Button
                            size="sm"
                            variant="outline"
                            data-testid={`edit-listing-${l.id}`}
                            onClick={() => navigate(`/edit-listing/${l.id}`)}
                            className="flex-1 gap-1 rounded-xl border-ppc"
                          >
                            <Pencil className="h-3.5 w-3.5" /> {t("act_edit")}
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            data-testid={`delete-listing-${l.id}`}
                            onClick={() => deleteListing(l.id)}
                            className="rounded-xl text-[#f0a0a8]"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </TabsContent>

        {/* Payouts */}
        <TabsContent value="payouts" className="mt-6">
          <PayoutPanel />
        </TabsContent>
      </Tabs>

      <Dialog open={!!reviewFor} onOpenChange={(o) => !o && setReviewFor(null)}>
        <DialogContent className="rounded-3xl border-ppc bg-ppc-surface" data-testid="review-dialog">
          <DialogHeader>
            <DialogTitle className="text-ppc-text">{t("rev_rate")}</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-ppc-muted">{reviewFor?.listing_title} · {reviewFor?.provider_name}</p>
          <div className="mt-2">
            <p className="text-[13px] text-ppc-muted">{t("rev_your_rating")}</p>
            <RatingStars
              size={26}
              value={reviewForm.rating}
              onChange={(n) => setReviewForm((f) => ({ ...f, rating: n }))}
              className="mt-1"
            />
          </div>
          <div className="mt-2">
            <p className="text-[13px] text-ppc-muted">{t("rev_comment")}</p>
            <Textarea
              data-testid="review-comment-input"
              value={reviewForm.comment}
              onChange={(e) => setReviewForm((f) => ({ ...f, comment: e.target.value }))}
              placeholder={t("rev_comment_ph")}
              className="mt-1.5 rounded-xl border-ppc bg-ppc-bg"
            />
          </div>
          <Button onClick={submitReview} data-testid="review-submit-btn" className="btn-primary mt-3 w-full rounded-xl">
            {t("rev_submit")}
          </Button>
        </DialogContent>
      </Dialog>
    </div>
  );
}
